/**
 * Layout constants shared between the in-drawer terminal and its
 * fixed-position side panel. Centralising the values prevents the
 * two from drifting — the side panel grows by `SIDEBAR_WIDTH` when
 * the sidebar opens, which must match the width that
 * `BlockTerminalTab` renders the sidebar at, or the chat pane
 * shrinks (or the panel ends up with a gap).
 */

/** Default sessions-sidebar width (matches Paradex's 280px). */
export const SIDEBAR_WIDTH = 280;
export const SIDEBAR_MIN_WIDTH = 200;
export const SIDEBAR_MAX_WIDTH = 480;

/**
 * Minimum width of the chat pane in the side-panel mount. The panel
 * grows when the sidebar opens so the chat pane never gets squished.
 */
export const CHAT_PANE_WIDTH = 420;

/** Side-panel chat-pane sizing (left edge of the side panel resizes this). */
export const SIDE_PANEL_CHAT_DEFAULT_WIDTH = CHAT_PANE_WIDTH;
export const SIDE_PANEL_CHAT_MIN_WIDTH = 360;
export const SIDE_PANEL_CHAT_MAX_WIDTH = 720;

const SIDEBAR_WIDTH_STORAGE_KEY_BASE = 'paradigm_terminal_sidebar_width';
const SIDE_PANEL_CHAT_WIDTH_STORAGE_KEY_BASE =
  'paradigm_terminal_side_panel_chat_width';

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function sidebarWidthKey(userId: string): string {
  return `${userId.toLowerCase()}:${SIDEBAR_WIDTH_STORAGE_KEY_BASE}`;
}

function sidePanelChatWidthKey(userId: string): string {
  return `${userId.toLowerCase()}:${SIDE_PANEL_CHAT_WIDTH_STORAGE_KEY_BASE}`;
}

export function loadSidebarWidth(userId: string | null): number {
  if (userId == null) return SIDEBAR_WIDTH;
  try {
    const raw = localStorage.getItem(sidebarWidthKey(userId));
    if (raw == null) return SIDEBAR_WIDTH;
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return SIDEBAR_WIDTH;
    return clamp(parsed, SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH);
  } catch {
    return SIDEBAR_WIDTH;
  }
}

export function persistSidebarWidth(userId: string, width: number): void {
  try {
    localStorage.setItem(sidebarWidthKey(userId), String(width));
  } catch {
    // storage unavailable — ignore
  }
}

export function loadSidePanelChatWidth(userId: string | null): number {
  if (userId == null) return SIDE_PANEL_CHAT_DEFAULT_WIDTH;
  try {
    const raw = localStorage.getItem(sidePanelChatWidthKey(userId));
    if (raw == null) return SIDE_PANEL_CHAT_DEFAULT_WIDTH;
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return SIDE_PANEL_CHAT_DEFAULT_WIDTH;
    return clamp(parsed, SIDE_PANEL_CHAT_MIN_WIDTH, SIDE_PANEL_CHAT_MAX_WIDTH);
  } catch {
    return SIDE_PANEL_CHAT_DEFAULT_WIDTH;
  }
}

export function persistSidePanelChatWidth(userId: string, width: number): void {
  try {
    localStorage.setItem(sidePanelChatWidthKey(userId), String(width));
  } catch {
    // storage unavailable — ignore
  }
}
